import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Layers } from "lucide-react";

const BG =
  "https://images.unsplash.com/photo-1534639077088-d702bcf685e7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwxfHxyYXclMjBmYWJyaWMlMjB0ZXh0dXJlfGVufDB8fHx8MTc4NTIzMTA3MHww&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("admin@wovenledger.com");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) navigate("/");
    else setError(res.error);
  };

  return (
    <div>
    <div className="min-h-screen flex">
      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 bg-[#F6F5F2]">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-2 mb-10">
            <div className="w-9 h-9 bg-[#1C2331] flex items-center justify-center rounded-sm">
              <Layers className="w-5 h-5 text-[#D95D39]" />
            </div>
            <div className="font-heading font-extrabold text-xl text-[#0A0D14] tracking-tight">Madhusudan Expofab</div>
          </div>

          <h1 className="font-heading text-3xl font-bold text-[#0A0D14] tracking-tight">Welcome back</h1>
          <p className="text-sm text-[#8CA3A7] mt-2 mb-8">Sign in to manage your fabric indents.</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs font-medium uppercase tracking-[0.1em] text-[#8CA3A7]">Email</label>
              <input
                data-testid="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 w-full rounded-sm border border-[#E6E4DD] bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#1C2331] focus:border-[#1C2331] transition-colors"
                required
              />
            </div>
            <div>
              <label className="text-xs font-medium uppercase tracking-[0.1em] text-[#8CA3A7]">Password</label>
              <input
                data-testid="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                className="mt-1.5 w-full rounded-sm border border-[#E6E4DD] bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#1C2331] focus:border-[#1C2331] transition-colors"
                required
              />
            </div>

            {error && (
              <div data-testid="login-error" className="text-sm text-[#991B1B] bg-[#FEF2F2] border border-[#FECACA] rounded-sm px-3 py-2">
                {error}
              </div>
            )}

            <button
              data-testid="login-submit"
              type="submit"
              disabled={loading}
              className="w-full bg-[#1C2331] text-white rounded-sm px-4 py-2.5 text-sm font-medium transition-transform active:scale-[0.98] hover:bg-[#2A3447] disabled:opacity-60"
            >
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>
        </div>
      </div>

      <div className="hidden lg:block w-1/2 relative">
        <img src={BG} alt="Greige fabric rolls" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/25" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <div className="font-heading text-3xl font-bold leading-tight">Every metre.<br />Every batch.<br />Every rupee.</div>
          <p className="text-sm text-white/80 mt-3 max-w-sm">Snap a purchase order, track deliveries in batches, and never miss a payment term again.</p>
        </div>
      </div>
    </div>

    <section className="bg-white px-6 py-16 border-t border-[#E6E4DD]">
      <div className="max-w-4xl mx-auto space-y-6">
        <header className="space-y-2">
          <h2 className="font-heading text-2xl font-bold text-[#0A0D14] tracking-tight">About Madhusudan Expofab</h2>
          <p className="text-sm text-[#4B5563] leading-relaxed">
            Madhusudan Expofab is a fabric indenting house that connects textile buyers with trusted mills and
            fabric sellers across India. As an indenting agent, we coordinate direct billing from the seller to the
            buyer, helping both sides transact with confidence across greige, cotton, viscose, polyester and
            specialty fabrics. Our work sits at the heart of the textile supply chain — matching the right quality
            and price to each requirement and keeping every order moving on time.
          </p>
        </header>

        <div className="grid md:grid-cols-3 gap-6">
          <article className="space-y-1.5">
            <h3 className="font-heading font-bold text-[#1C2331]">Purchase Order Tracking</h3>
            <p className="text-sm text-[#4B5563] leading-relaxed">
              Every purchase order is recorded with buyer, seller, fabric type, quantity in metres, rate and agreed
              payment terms. Uploading a photo of the PO auto-fills the details, so nothing is missed and history
              stays searchable for the whole team.
            </p>
          </article>
          <article className="space-y-1.5">
            <h3 className="font-heading font-bold text-[#1C2331]">Batch-wise Deliveries</h3>
            <p className="text-sm text-[#4B5563] leading-relaxed">
              Fabric rarely ships in one go. We log each delivery challan and invoice against its order, tracking
              delivered versus pending metres batch by batch — with the grand-total invoice value captured straight
              from the document for accurate accounting.
            </p>
          </article>
          <article className="space-y-1.5">
            <h3 className="font-heading font-bold text-[#1C2331]">Payment & Aging Control</h3>
            <p className="text-sm text-[#4B5563] leading-relaxed">
              Payment due dates are calculated from each invoice and its credit terms. Aging buckets, overdue
              spotlights and party ledgers make it clear who owes what, while daily and weekly email reports keep
              collections on schedule.
            </p>
          </article>
        </div>

        <p className="text-sm text-[#4B5563] leading-relaxed">
          Built for a real fabric business, this platform brings purchase orders, deliveries, invoices and payments
          into one place. Managers get a live dashboard of order value, outstanding amounts and category-wise seller
          exposure, plus downloadable PDF statements and Google Sheets exports for buyers, sellers and accountants.
          Role-based logins let the business owner control who can view, edit or manage the data, with a full audit
          trail of every change. From the first indent to the final rupee received, Madhusudan Expofab keeps fabric
          trade organised, transparent and dependable.
        </p>

        <p className="text-xs text-[#8CA3A7]">© {new Date().getFullYear()} Madhusudan Expofab · Fabric indenting &amp; textile order management.</p>
      </div>
    </section>
    </div>
  );
}
